/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, FormControlLabel, Grid, MenuItem, Popover } from '@mui/material';
import { Field, Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { difference, find, isEmpty, map } from 'lodash';

import TextField from 'common/forms/TextField';
import SelectField from 'common/forms/SelectField';
import DateRangePickerField from 'common/forms/DateRangePickerField';
import { validateMinLength } from 'common/forms/formValidations';
import MultiPracticeSelectField from 'common/forms/MultiPracticeSelectField';
import { casingFormat } from 'common/utils';
import { getPartnerPractices } from 'store/thunks/partnerThunks';
import { getStateDisplayName } from '../../../store/utils/shipmentUtils';
import { selectAccessibleFacilities } from '../../../store/selectors/shipmentModuleSelectors';
import { updateShipmentFilter } from '../../../store/thunks/shipmentModuleThunks';

const SHIPPING_CHOICE_OPTIONS = [
  { value: 'FEDEX_2DAY', label: 'FedEx 2Day\u00AE' },
  { value: 'FEDEX_STANDARD_OVERNIGHT', label: 'FedEx Standard Overnight\u00AE' },
  { value: 'FEDEX_PRIORITY_OVERNIGHT', label: 'FedEx Priority Overnight\u00AE' },
];

const ShipmentsFilterButton = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [practices, setPractices] = useState([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  const [formInitialValues, setFormInitialValues] = useState({});
  const [isLoadingPractices, setIsLoadingPractices] = useState(false);
  const [shipDateRange, setShipDateRange] = useState(null);
  const [shippedDateRange, setShippedDateRange] = useState(null);
  const [voidedDateRange, setVoidedDateRange] = useState(null);

  const { activeCategory, activeSubcategory, activeFacility } = useSelector(
    ({ shipmentModule }) => shipmentModule
  );
  const filter = useSelector(({ shipmentModule }) => shipmentModule.list.filter);

  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);
  const userAuth = useSelector(({ auth }) => auth);
  const fulfillingPharmacies = userAuth?.fulfillingPharmacies;
  const facilities = useSelector(selectAccessibleFacilities);

  // Hide facility filter when already on a specific facility
  const isOnSpecificFacility =
    (activeCategory === 'awaitingShipment' && activeSubcategory !== 'all') ||
    (activeCategory === 'shipped' && activeFacility !== 'all');

  const popoverId = anchorEl ? 'Shipments-FilterButton-Popover' : undefined;

  const partnerOptions = useMemo(
    () =>
      partners?.map(({ partnerName, partnerUuid }) => ({
        value: partnerUuid,
        label: casingFormat(partnerName),
      })) ?? [],
    [partners]
  );

  const practiceOptions = useMemo(
    () =>
      practices?.map(({ practiceId, practiceName }) => ({
        value: practiceId,
        label: casingFormat(practiceName),
      })) ?? [],
    [practices]
  );

  useEffect(() => {
    if (isEmpty(selectedPartnerIds)) {
      setPractices([]);
      setIsLoadingPractices(false);
      return;
    }

    setIsLoadingPractices(true);
    dispatch(getPartnerPractices({ partnerIds: selectedPartnerIds }))
      .then((practice) => {
        if (practice) {
          setPractices(practice);
        }
      })
      .finally(() => {
        setIsLoadingPractices(false);
      });
  }, [selectedPartnerIds, dispatch]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);

    const selectedPartners = partnerOptions.filter((partner) =>
      filter?.partnerIds?.includes(partner.value)
    );

    const partnerIds = selectedPartners.map((partner) => partner.value);
    if (!isEmpty(partnerIds)) {
      setSelectedPartnerIds(partnerIds);
    }

    const selectedPractices = practiceOptions.filter((practice) =>
      filter?.practiceIds?.includes(practice.value)
    );

    const existingShipDateRange =
      filter?.shipDateAfter || filter?.shipDateBefore
        ? [filter.shipDateAfter, filter.shipDateBefore].filter(Boolean)
        : null;
    setShipDateRange(existingShipDateRange);

    const existingShippedDateRange =
      filter?.shippedDateAfter || filter?.shippedDateBefore
        ? [filter.shippedDateAfter, filter.shippedDateBefore].filter(Boolean)
        : null;
    setShippedDateRange(existingShippedDateRange);

    const existingVoidedDateRange =
      filter?.voidedDateAfter || filter?.voidedDateBefore
        ? [filter.voidedDateAfter, filter.voidedDateBefore].filter(Boolean)
        : null;
    setVoidedDateRange(existingVoidedDateRange);

    const selectedShippingChoices = SHIPPING_CHOICE_OPTIONS.filter((opt) =>
      filter?.carrierServiceCodes?.includes(opt.value)
    );

    setFormInitialValues({
      recipientName: filter?.recipientName,
      orderId: filter?.referenceId,
      trackingNumber: filter?.trackingNumber,
      fulfillingPharmacyIds: filter?.fulfillingPharmacyIds?.[0] || '',
      partnerIds: selectedPartners,
      practiceIds: selectedPractices,
      carrierServiceCodes: selectedShippingChoices,
      isManualShipment: filter?.isManualShipment || false,
      hasTags: filter?.hasTags || false,
      tagLabels: filter?.tagLabels?.join(', ') || '',
    });
  };

  const handleClose = () => setAnchorEl(null);

  const handlePartnerChange = (newValue, form) => {
    const partnerIds = newValue.map((partner) => partner.value);
    const removedPartnerIds = difference(selectedPartnerIds, partnerIds);

    setSelectedPartnerIds(partnerIds);

    if (!isEmpty(removedPartnerIds)) {
      const currentPractices = form.getState().values.practiceIds || [];
      const updatedPractices = currentPractices.filter(({ value: practiceId }) => {
        const practice = practices.find((p) => p.practiceId === practiceId);
        return practice && !removedPartnerIds.includes(practice.partnerId);
      });
      form.change('practiceIds', updatedPractices);
    }

    form.change('partnerIds', newValue);

    if (isEmpty(partnerIds)) {
      setPractices([]);
      form.change('practiceIds', []);
    }
  };

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    const newFilter = {
      recipientName: formData.recipientName || undefined,
      referenceId: formData.orderId || undefined,
      trackingNumber: formData.trackingNumber || undefined,
      partnerIds: !isEmpty(formData.partnerIds)
        ? formData.partnerIds.map((p) => p.value)
        : undefined,
      practiceIds: !isEmpty(formData.practiceIds)
        ? formData.practiceIds.map((p) => p.value)
        : undefined,
    };

    // Only include facility filter if not already on a specific facility
    if (!isOnSpecificFacility) {
      newFilter.fulfillingPharmacyIds = formData.fulfillingPharmacyIds
        ? [formData.fulfillingPharmacyIds]
        : undefined;
    }

    // Single date selection filters for that exact date; range uses both bounds
    newFilter.shipDateAfter = shipDateRange?.[0] || undefined;
    newFilter.shipDateBefore = shipDateRange?.[1] || shipDateRange?.[0] || undefined;
    newFilter.shippedDateAfter = shippedDateRange?.[0] || undefined;
    newFilter.shippedDateBefore = shippedDateRange?.[1] || shippedDateRange?.[0] || undefined;
    newFilter.voidedDateAfter = voidedDateRange?.[0] || undefined;
    newFilter.voidedDateBefore = voidedDateRange?.[1] || voidedDateRange?.[0] || undefined;
    newFilter.carrierServiceCodes = !isEmpty(formData.carrierServiceCodes)
      ? formData.carrierServiceCodes.map((opt) => opt.value)
      : undefined;
    newFilter.isManualShipment = formData.isManualShipment || undefined;
    newFilter.hasTags = formData.hasTags || undefined;
    newFilter.tagLabels = formData.tagLabels
      ? formData.tagLabels
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined;
    if (newFilter.tagLabels?.length === 0) newFilter.tagLabels = undefined;

    dispatch(updateShipmentFilter({ filter: newFilter }));
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Grid
          container
          sx={{ p: 1.5, width: 325, maxHeight: '80vh', overflowY: 'auto' }}
          spacing={2}
          direction='column'
        >
          <Grid item>
            <Form
              initialValues={formInitialValues}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid, form }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Recipient Name'
                      name='recipientName'
                      id='Shipments-FilterButton-recipientName-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Order Id'
                      name='orderId'
                      id='Shipments-FilterButton-orderId-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Tracking Number'
                      name='trackingNumber'
                      id='Shipments-FilterButton-trackingNumber-input'
                    />
                  </Grid>

                  {!isOnSpecificFacility && (
                    <Grid item sx={{ py: 0.5 }}>
                      <SelectField
                        label='Facility'
                        name='fulfillingPharmacyIds'
                        id='Shipments-FilterButton-facility-dropdown'
                      >
                        <MenuItem value=''>All</MenuItem>
                        {map(
                          fulfillingPharmacies,
                          ({ fulfillingPharmacyId, fulfillingPharmacyName }) => {
                            const facilityState = find(
                              facilities,
                              (f) => f.fulfillingPharmacyId === fulfillingPharmacyId
                            )?.state;
                            return (
                              <MenuItem key={fulfillingPharmacyId} value={fulfillingPharmacyId}>
                                {facilityState
                                  ? `${fulfillingPharmacyName} (${getStateDisplayName(facilityState)})`
                                  : fulfillingPharmacyName}
                              </MenuItem>
                            );
                          }
                        )}
                      </SelectField>
                    </Grid>
                  )}

                  <Grid item sx={{ py: 0.5 }}>
                    <DateRangePickerField
                      label='Ship Date'
                      initialValue={shipDateRange}
                      onDateChange={(dateRange) => setShipDateRange(dateRange)}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateRangePickerField
                      label='Shipped Date'
                      initialValue={shippedDateRange}
                      onDateChange={(dateRange) => setShippedDateRange(dateRange)}
                    />
                  </Grid>

                  {activeSubcategory === 'voided' && (
                    <Grid item sx={{ py: 0.5 }}>
                      <DateRangePickerField
                        label='Voided Date'
                        initialValue={voidedDateRange}
                        onDateChange={(dateRange) => setVoidedDateRange(dateRange)}
                      />
                    </Grid>
                  )}

                  <MultiPracticeSelectField
                    label='Shipping Choice'
                    name='carrierServiceCodes'
                    id='Shipments-FilterButton-shippingChoice-dropdown'
                    options={SHIPPING_CHOICE_OPTIONS}
                  />

                  <MultiPracticeSelectField
                    label='Partner'
                    name='partnerIds'
                    id='Shipments-FilterButton-partnerIds-dropdown'
                    options={partnerOptions}
                    onChange={(event, newValue) => {
                      handlePartnerChange(newValue, form);
                    }}
                  />

                  <MultiPracticeSelectField
                    label='Practice'
                    name='practiceIds'
                    id='Shipments-FilterButton-practiceIds-dropdown'
                    options={practiceOptions}
                    disabled={isEmpty(selectedPartnerIds) || isEmpty(practiceOptions)}
                    loading={isLoadingPractices}
                  />

                  <Grid item sx={{ py: 0.5 }}>
                    <Field name='isManualShipment' type='checkbox'>
                      {({ input }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              name={input.name}
                              checked={!!input.checked}
                              onChange={input.onChange}
                              onBlur={input.onBlur}
                              onFocus={input.onFocus}
                            />
                          }
                          label='Manual Shipments Only'
                        />
                      )}
                    </Field>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <Field name='hasTags' type='checkbox'>
                      {({ input }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              name={input.name}
                              checked={!!input.checked}
                              onChange={input.onChange}
                              onBlur={input.onBlur}
                              onFocus={input.onFocus}
                            />
                          }
                          label='Has Tags'
                        />
                      )}
                    </Field>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Tag Labels (comma-separated)'
                      name='tagLabels'
                      id='Shipments-FilterButton-tagLabels-input'
                    />
                  </Grid>

                  <Grid container justifyContent='flex-end'>
                    <Button variant='contained' type='submit' disabled={invalid}>
                      Apply
                    </Button>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default ShipmentsFilterButton;
